import { createStereoInputConstraints } from "./types";

/**
 * Result of getting a channel-routed audio source.
 */
export interface ChannelRoutedSource {
  /** The output node to connect to your destination */
  output: AudioNode;
  /** Call this to disconnect all nodes */
  cleanup: () => void;
}

/**
 * Represents an audio input stream from a device.
 *
 * Encapsulates the raw MediaStream and provides:
 * - Channel count detection
 * - Channel-routed source creation
 * - Cleanup handling
 *
 * @example
 * ```ts
 * const inputStream = new AudioInputStream(deviceId);
 * await inputStream.init();
 *
 * console.log(inputStream.channelCount); // 1 or 2
 *
 * const source = inputStream.getSource(audioContext, 'stereo');
 * source.output.connect(destination);
 *
 * // When done:
 * source.cleanup();
 * inputStream.stop();
 * ```
 */
export class AudioInputStream {
  readonly deviceId: string;
  private _stream: MediaStream | null = null;
  private _channelCount: number = 1;

  constructor(deviceId: string) {
    this.deviceId = deviceId;
  }

  /**
   * Initialize the stream by requesting access to the device.
   * Must be called before accessing stream or channelCount.
   */
  async init(): Promise<void> {
    this._stream = await navigator.mediaDevices.getUserMedia({
      audio: createStereoInputConstraints(this.deviceId),
    });

    this._channelCount = this.detectChannelCount(this._stream);
  }

  /**
   * The underlying MediaStream.
   * @throws Error if init() has not been called
   */
  get stream(): MediaStream {
    if (!this._stream) {
      throw new Error("AudioInputStream not initialized. Call init() first.");
    }
    return this._stream;
  }

  /**
   * Number of channels in the stream (1 for mono, 2 for stereo).
   */
  get channelCount(): number {
    return this._channelCount;
  }

  /**
   * Detect the number of channels in a stream.
   */
  private detectChannelCount(stream: MediaStream): number {
    const audioTrack = stream.getAudioTracks()[0];
    const settings = audioTrack?.getSettings();

    if (settings?.channelCount !== undefined) {
      return settings.channelCount;
    }

    // Fallback: check the mediaStreamTrack's getCapabilities if available
    const capabilities = audioTrack?.getCapabilities?.();
    if (capabilities?.channelCount) {
      // channelCount capability is a range {min, max}, use max
      const maxChannels = typeof capabilities.channelCount === 'object'
        ? (capabilities.channelCount as { max?: number }).max
        : capabilities.channelCount;
      return maxChannels ?? 1;
    }

    return 1;
  }

  /**
   * Get a channel-routed audio source from this stream.
   *
   * @param audioContext - The AudioContext to create nodes in
   * @param channel - Which channel(s) to route: 'left', 'right', or 'stereo'
   * @returns The output node and cleanup function
   */
  getSource(
    audioContext: AudioContext,
    channel: 'left' | 'right' | 'stereo'
  ): ChannelRoutedSource {
    const source = audioContext.createMediaStreamSource(this.stream);

    if (channel === 'stereo') {
      return {
        output: source,
        cleanup: () => source.disconnect(),
      };
    }

    // Route only the selected channel (left or right)
    const splitter = audioContext.createChannelSplitter(2);
    const merger = audioContext.createChannelMerger(1);

    source.connect(splitter);
    const channelIndex = channel === 'left' ? 0 : 1;
    splitter.connect(merger, channelIndex, 0);

    return {
      output: merger,
      cleanup: () => {
        source.disconnect();
        splitter.disconnect();
        merger.disconnect();
      },
    };
  }

  /**
   * Stop the stream and release the microphone.
   */
  stop(): void {
    if (this._stream) {
      this._stream.getTracks().forEach(track => track.stop());
      this._stream = null;
    }
  }
}
